'use client';

import { useWallet } from '@solana/wallet-adapter-react';
import { useEffect, useState, useRef } from 'react';
import { useSupabase } from '../auth/auth-provider';

export function WalletListener() {
  // Grab the wallet context once at the top-level (never call hooks inside callbacks)
  const wallet = useWallet();
  const connected = wallet.connected;
  const publicKey = wallet.publicKey;
  const {web3Profile,supabase,session } = useSupabase();
  const [isProcessing, setIsProcessing] = useState(false);
  const lastProcessedWallet = useRef<string | null>(null);

  useEffect(() => {
    // CRITICAL: Do NOT run ANY wallet logic if user is not logged in
    // This prevents ALL wallet-signin and profile calls on the login page
    // Check this BEFORE creating the handler function
    if (!session?.user) {
      return; // No session - user hasn't logged in yet, skip entire effect
    }

    const handleWalletConnection = async () => {
      // Double-check session still exists (safety)
      if (!session?.user) {
        return;
      }

      // CRITICAL: Skip ALL wallet logic for username/password login users
      // Check this SECOND - only after confirming user is logged in
      if (session.user.user_metadata?.is_web3_user !== true) {
        return; // Username/password user - skip ALL wallet logic including signin calls
      }

      // SECURITY: Only process wallet connections for users who are already logged in
      // Do NOT automatically create accounts when wallets connect
      if (!connected || !publicKey || isProcessing) {
        return;
      }

      const currentWallet = publicKey.toBase58();
      try {
        // Expose the current WalletAdapter (not the React hook wrapper)
        const adapter = (wallet as any)?.wallet?.adapter || (window as any)?.solana || null
        ;(window as any).umiWalletAdapter = adapter
      } catch {}

      // If user already has a Web3 profile and is logged in, update their session
      if (web3Profile && session) {
        // console.log('✅ Authenticated user connected wallet, updating session...');

        // Verify the connected wallet matches their profile
        if (web3Profile.wallet_address?.toLowerCase() === currentWallet.toLowerCase()) {
          // console.log('✅ Wallet matches authenticated user profile');
        } else {
          // console.warn('⚠️ Connected wallet does not match user profile');
          // Optionally disconnect or warn user
          // await disconnect();
          return;
        }

        lastProcessedWallet.current = currentWallet;
        return;
      }

      // Wallet-signin removed - using username/password login only
      // If user is logged in but doesn't have a Web3 profile, they can link wallet manually
      if (session?.user && !web3Profile) {
        // User can link wallet via account settings if needed
        return;
      }

      // Wallet-signin removed - using username/password login only
      // Auto-login via wallet is disabled - users must use username/password login
    };

    handleWalletConnection();
  }, [connected, publicKey, web3Profile, isProcessing, session]);

  // Reset when wallet disconnects
  useEffect(() => {
    if (!connected) {
      lastProcessedWallet.current = null;
      setIsProcessing(false);
      try { (window as any).umiWalletAdapter = null } catch {}
    }
  }, [connected]);

  return null;
}